import { resolveMove } from "./helpers";
import type { MoveOutcome, TileValue } from "./types";

export type TutorialStepId =
  | "intro_hand"
  | "match_board"
  | "ten_hand"
  | "ten_board"
  | "build_hand"
  | "build_board"
  | "clear_eight_hand"
  | "clear_eight_board"
  | "done";

type TutorialSelectHandStep = {
  id: TutorialStepId;
  kind: "select_hand";
  text: string;
  expectedHandIndex: number;
};

export type TutorialMoveStep = {
  id: TutorialStepId;
  kind: "tap_board";
  text: string;
  expectedHandIndex: number;
  expectedBoardIndex: number;
  expectedOutcome: Extract<MoveOutcome, "match_clear" | "build_to_ten_clear" | "build">;
  refillTile: TileValue | null;
};

type TutorialAckStep = {
  id: TutorialStepId;
  kind: "ack";
  text: string;
};

export type TutorialStep = TutorialSelectHandStep | TutorialMoveStep | TutorialAckStep;

export const ROUND_ZERO_TUTORIAL_INITIAL_BOARD = [4, 3, 5, 9, 6] as const;
export const ROUND_ZERO_TUTORIAL_INITIAL_HAND = [4, 7, 3] as const;

export const ROUND_ZERO_TUTORIAL_STEPS = [
  {
    id: "intro_hand",
    kind: "select_hand",
    text: "Clear the board by playing tiles from your hand.\n\nTap the 4 in your hand to start.",
    expectedHandIndex: 0
  },
  {
    id: "match_board",
    kind: "tap_board",
    text: "Match numbers to clear tiles.\n\nTap the 4 on the table.",
    expectedHandIndex: 0,
    expectedBoardIndex: 0,
    expectedOutcome: "match_clear",
    refillTile: 1
  },
  {
    id: "ten_hand",
    kind: "select_hand",
    text: "Adding to 10 clears tiles too.\n\nTap the 7 in your hand.",
    expectedHandIndex: 0
  },
  {
    id: "ten_board",
    kind: "tap_board",
    text: "Tap the 3 on the table.",
    expectedHandIndex: 0,
    expectedBoardIndex: 0,
    expectedOutcome: "build_to_ten_clear",
    refillTile: 6
  },
  {
    id: "build_hand",
    kind: "select_hand",
    text: "Add tiles to change the numbers on the board.\n\nTap the 3 in your hand.",
    expectedHandIndex: 0
  },
  {
    id: "build_board",
    kind: "tap_board",
    text: "Tap the 5 on the board to build to 8.",
    expectedHandIndex: 0,
    expectedBoardIndex: 0,
    expectedOutcome: "build",
    refillTile: 8
  },
  {
    id: "clear_eight_hand",
    kind: "select_hand",
    text: "Now you can clear the 8.\n\nTap the 8 in your hand.",
    expectedHandIndex: 2
  },
  {
    id: "clear_eight_board",
    kind: "tap_board",
    text: "Tap the 8 tile on the table.",
    expectedHandIndex: 2,
    expectedBoardIndex: 0,
    expectedOutcome: "match_clear",
    refillTile: null
  },
  {
    id: "done",
    kind: "ack",
    text: "Now you know the basics.\n\nYou'll learn more as you play."
  }
] as const satisfies readonly TutorialStep[];

export function applyTutorialStepMove(
  boardValues: readonly TileValue[],
  handValues: readonly TileValue[],
  step: TutorialMoveStep
): {
  board: TileValue[];
  hand: TileValue[];
} {
  const handValue = handValues[step.expectedHandIndex];
  const boardValue = boardValues[step.expectedBoardIndex];

  if (handValue === undefined || boardValue === undefined) {
    throw new Error(`Tutorial step ${step.id} is missing its scripted tiles.`);
  }

  const resolution = resolveMove(handValue, boardValue);

  if (resolution.outcome !== step.expectedOutcome) {
    throw new Error(`Tutorial step ${step.id} is misconfigured.`);
  }

  return {
    board: boardValues.flatMap((value, index) => {
      if (index !== step.expectedBoardIndex) {
        return [value];
      }

      if (resolution.clearsTile) {
        return [];
      }

      return resolution.builtValue === null ? [] : [resolution.builtValue];
    }),
    hand:
      step.refillTile === null
        ? handValues.filter((_, index) => index !== step.expectedHandIndex)
        : [
            ...handValues.filter((_, index) => index !== step.expectedHandIndex),
            step.refillTile
          ]
  };
}
